import Vue from 'vue'
import axios from 'axios'
import factory from '../factory'

const store = factory('Tournaments')

store.actions.getCompetitors = async (_, id) => {
  const response = await axios.get(`tournament/${id}/competitors`)
  return response.data
}

store.actions.getTeams = async (_, id) => {
  const response = await axios.get(`tournament/${id}/teams`)
  return response.data
}

store.actions.getHistory = async (_, id) => {
  const response = await axios.get(`tournament/${id}/history`)
  return response.data
}

store.actions.registerCompetitor = async ({ commit }, { id, payload }) => {
  const response = await axios.post(`tournament/${id}/register-competitor`, payload)
  commit('update', response.data)
  commit('setSingle', response.data)
  return response.data
}

store.actions.updateCompetitor = async ({ commit }, { id, payload }) => {
  const response = await axios.put(`tournament/competitor/${id}`, payload)
  commit('update', response.data)
  commit('setSingle', response.data)
  return response.data
}

store.actions.removeCompetitor = async ({ commit }, id) => {
  const response = await axios.delete(`tournament/competitor/${id}`)
  commit('update', response.data)
  commit('setSingle', response.data)
  return response.data
}

store.actions.registerTeam = async ({ commit }, { id, payload }) => {
  const response = await axios.post(`tournament/${id}/register-team`, payload)
  commit('update', response.data)
  commit('setSingle', response.data)
  return response.data
}

store.actions.updateTeam = async ({ commit }, { id, payload }) => {
  const response = await axios.put(`tournament/team/${id}`, payload)
  commit('update', response.data)
  commit('setSingle', response.data)
  return response.data
}

store.actions.removeTeam = async ({ commit }, id) => {
  const response = await axios.delete(`tournament/team/${id}`)
  commit('update', response.data)
  commit('setSingle', response.data)
  return response.data
}

store.actions.registerReferee = async ({ commit }, { id, refereeId }) => {
  const response = await axios.post(`tournament/${id}/register-referee/${refereeId}`)
  commit('update', response.data)
  commit('setSingle', response.data)
  return response.data
}

store.actions.removeReferee = async ({ commit }, { id, refereeId }) => {
  const response = await axios.delete(`tournament/${id}/referee/${refereeId}`)
  commit('update', response.data)
  commit('setSingle', response.data)
  return response.data
}

store.actions.registerCoach = async ({ commit }, { id, coachId }) => {
  const response = await axios.post(`tournament/${id}/register-coach/${coachId}`)
  commit('update', response.data)
  commit('setSingle', response.data)
  return response.data
}

store.actions.removeCoach = async ({ commit }, { id, coachId }) => {
  const response = await axios.delete(`tournament/${id}/coach/${coachId}`)
  commit('update', response.data)
  commit('setSingle', response.data)
  return response.data
}

store.actions.registerOfficial = async ({ commit }, { id, officialId }) => {
  const response = await axios.post(`tournament/${id}/register-official/${officialId}`)
  commit('update', response.data)
  commit('setSingle', response.data)
  return response.data
}

store.actions.removeOfficial = async ({ commit }, { id, officialId }) => {
  const response = await axios.delete(`tournament/${id}/official/${officialId}`)
  commit('update', response.data)
  commit('setSingle', response.data)
  return response.data
}

store.actions.archive = async ({ commit }, id) => {
  const response = await axios.put(`tournament/${id}/archive`)
  commit('update', response.data)
  commit('setSingle', response.data)
  return response.data
}

store.actions.getBrackets = async (_, id) => {
  const response = await axios.get(`tournament/${id}/bracket`)
  // Standardized response: always returns { brackets: [] }
  return response.data.brackets || []
}

store.actions.generateBrackets = async (_, id) => {
  const response = await axios.post(`tournament/${id}/bracket/generate`)
  return response.data
}

store.actions.generateMissingBrackets = async (_, id) => {
  const response = await axios.post(`tournament/${id}/bracket/generate`, { onlyMissing: true })
  return response.data
}

store.actions.regenerateBracket = async (_, bracketId) => {
  const response = await axios.post(`bracket/${bracketId}/regenerate`)
  return response.data
}

store.actions.resetBrackets = async (_, id) => {
  const response = await axios.delete(`tournament/${id}/bracket/reset`)
  return response.data
}

store.actions.submitMatchResult = async (_, { matchId, winnerId }) => {
  const response = await axios.post(`matches/${matchId}/result`, {
    winner_id: winnerId
  })
  return response.data
}

store.actions.updateMatchCompetitors = async (_, { matchId, competitorRegId, competitorTwoRegId }) => {
  const response = await axios.put(`matches/${matchId}/competitors`, {
    competitor_reg_id: competitorRegId,
    competitor_two_reg_id: competitorTwoRegId
  })
  return response.data
}

store.actions.updateBracketPlacement = async (_, { bracketId, placement }) => {
  const response = await axios.put(`bracket/${bracketId}/placement`, {
    placement_first_reg_id: placement.first ?? null,
    placement_second_reg_id: placement.second ?? null,
    placement_third_reg_id: placement.third ?? null,
    placement_third2_reg_id: placement.third2 ?? null
  })
  return response.data
}

store.actions.updateBracketPlacementMulti = async (_, { bracketId, placement }) => {
  const response = await axios.put(`bracket/${bracketId}/placement-multi`, {
    placement_first_reg_ids: Array.isArray(placement.first) ? placement.first : [],
    placement_second_reg_ids: Array.isArray(placement.second) ? placement.second : [],
    placement_third_reg_ids: Array.isArray(placement.third) ? placement.third : [],
    placement_third2_reg_ids: Array.isArray(placement.third2) ? placement.third2 : []
  })
  return response.data
}

/**
 * Persists judge scores for the Bodovanje draw model.
 * `scores` is [{ competitorRegId, values: [s1..s6] }]; only the listed rows are touched server-side.
 */
store.actions.updateBracketScores = async (_, { bracketId, scores }) => {
  const response = await axios.put(`bracket/${bracketId}/scores`, {
    scores: (Array.isArray(scores) ? scores : []).map(row => ({
      competitor_reg_id: row.competitorRegId,
      values: row.values
    }))
  })
  return response.data
}

store.actions.updateBracketDrawModel = async (_, { bracketId, drawModel, generalRanking }) => {
  const response = await axios.put(`bracket/${bracketId}/draw-model`, {
    draw_model: drawModel === 'scoring' ? 'scoring' : 'skeleton',
    general_ranking: generalRanking === true
  })
  return response.data
}

store.actions.lockBrackets = async ({ commit }, tournamentId) => {
  const response = await axios.post(`tournament/${tournamentId}/brackets/lock`)
  commit('update', response.data)
  commit('setSingle', response.data)
  return response.data
}

store.actions.unlockBrackets = async ({ commit }, tournamentId) => {
  const response = await axios.post(`tournament/${tournamentId}/brackets/unlock`)
  commit('update', response.data)
  commit('setSingle', response.data)
  return response.data
}

store.actions.awardParticipation = async (_, tournamentId) => {
  const response = await axios.post(`tournament/${tournamentId}/brackets/award-participation`)
  return response.data
}

store.mutations.removeImage = async (state, { tournament, id }) => {
  const newImages = (tournament.images || []).filter(i => i.id !== id)
  Vue.set(state.singles[tournament.id], 'images', newImages)
}

store.mutations.removeDocument = async (state, { tournament, id }) => {
  const newDocuments = (tournament.documents || []).filter(d => d.id !== id)
  Vue.set(state.singles[tournament.id], 'documents', newDocuments)
}

store.mutations.removeClubDeduction = async (state, { tournament, id }) => {
  const newClubDeductions = (tournament.clubDeductions || []).filter(d => d.id !== id)
  Vue.set(state.singles[tournament.id], 'clubDeductions', newClubDeductions)
}

store.mutations.addClubDeduction = async (state, { tournament, deduction }) => {
  const newClubDeductions = [...(tournament.clubDeductions || []), deduction]
  Vue.set(state.singles[tournament.id], 'clubDeductions', newClubDeductions)
}

store.mutations.updateClubDeduction = async (state, { tournament, deduction }) => {
  const newClubDeductions = (tournament.clubDeductions || []).map(d => d.id === deduction.id ? deduction : d)
  Vue.set(state.singles[tournament.id], 'clubDeductions', newClubDeductions)
}

store.actions.uploadCertificate = async ({ commit }, { tournamentId, certificateType, data }) => {
  const formData = new FormData()

  // Add PNG data if provided (from canvas export)
  if (data.png) {
    formData.append('png', data.png)
  }

  // Add file if provided
  if (data.imageFile) {
    formData.append('file', data.imageFile)
  }

  formData.append('type', certificateType)

  // Canvas JSON is stored but not used for file storage - it's optional metadata
  if (data.canvas_json) {
    formData.append('canvas_json', JSON.stringify(data.canvas_json))
  }

  // Text zone layout and settings (new format)
  if (data.text_zone) {
    formData.append('text_zone', JSON.stringify(data.text_zone))
  }

  const response = await axios.post(`tournament/${tournamentId}/certificate/upload`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
  return response.data
}

store.actions.getCertificates = async (_, tournamentId) => {
  const response = await axios.get(`tournament/${tournamentId}/certificates`)
  return response.data
}

store.actions.deleteCertificate = async ({ commit }, { tournamentId, certificateType }) => {
  // Call the delete endpoint - backend MUST handle:
  // 1. Removing the certificate file from storage
  // 2. Setting certificate field to NULL in tournament record (not empty string, must be null)
  // 3. Setting canvas_json field to NULL
  // 4. Persisting changes to database
  // 5. Returning updated tournament object or certificate: null in response
  const response = await axios.post(`tournament/${tournamentId}/certificate/delete`, {
    type: certificateType
  })

  // If the delete endpoint returns updated tournament data, update the store
  if (response.data && response.data.tournament) {
    commit('update', response.data.tournament)
    commit('setSingle', response.data.tournament)
  }

  // CRITICAL: Refetch certificates to verify backend actually set field to null
  // Backend MUST return null, not empty string or old path
  const certificatesResponse = await axios.get(`tournament/${tournamentId}/certificates`)

  // Verify backend properly set certificate to null
  const certificateValue = certificatesResponse.data && certificatesResponse.data[certificateType]
  const isDeleted = certificateValue === null || certificateValue === undefined || certificateValue === ''

  // Log for debugging - backend should return null
  console.log('Certificate delete - backend verification:', {
    certificateType,
    value: certificateValue,
    isNull: certificateValue === null,
    isUndefined: certificateValue === undefined,
    isEmpty: certificateValue === '',
    isDeleted,
    expected: 'null',
    actual: certificateValue === null ? 'null' : `"${certificateValue}"`,
    backendFixed: isDeleted
  })

  // If backend didn't set to null, throw error so frontend can handle it
  if (!isDeleted) {
    const error = new Error(`Backend delete failed: certificate field is not null. Value: "${certificateValue}". Backend must set certificate to null and persist to database.`)
    error.response = {
      data: {
        error: 'Backend did not remove certificate. Certificate field must be set to null.',
        certificateValue,
        certificateType
      }
    }
    throw error
  }

  return certificatesResponse.data
}

store.actions.getDocumentTypes = async (_, tournamentId) => {
  const response = await axios.get(`tournament/${tournamentId}/document-types`)
  return response.data
}

store.actions.createDocumentType = async (_, { tournamentId, name, imageFile }) => {
  const formData = new FormData()
  formData.append('name', name)
  formData.append('file', imageFile)
  const response = await axios.post(`tournament/${tournamentId}/document-types`, formData)
  return response.data
}

store.actions.updateDocumentTypeFields = async (_, { tournamentId, typeId, fields }) => {
  const response = await axios.post(`tournament/${tournamentId}/document-types/${typeId}/fields`, { fields })
  return response.data
}

store.actions.renameDocumentType = async (_, { tournamentId, typeId, name }) => {
  const response = await axios.post(`tournament/${tournamentId}/document-types/${typeId}/rename`, { name })
  return response.data
}

store.actions.replaceDocumentTypeImage = async (_, { tournamentId, typeId, imageFile }) => {
  const formData = new FormData()
  formData.append('file', imageFile)
  const response = await axios.post(`tournament/${tournamentId}/document-types/${typeId}/image`, formData)
  return response.data
}

store.actions.deleteDocumentType = async (_, { tournamentId, typeId }) => {
  const response = await axios.post(`tournament/${tournamentId}/document-types/${typeId}/delete`)
  return response.data
}

export default store
